import React from 'react';
import moment from 'moment';
import find from 'lodash/find';
import findLast from 'lodash/findLast';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useNavigate } from 'react-router';
import { useTranslation } from 'common/i18n/hooks';
import { Experiment, ExperimentCollectionType } from 'apps/experiments/src/types';
import { useExperimentPageContext } from 'iron/apps/experiments/src/components/context';
import { eventDetectionQueryKey } from 'apps/experiments/src/react-query';
import { queryModule } from 'common/report/report-manager/query';
import { BookmarkType } from 'common/report/queries/app-api/bookmark-client';
import { getLocation } from 'common/util/url';
import { useQueryFeatureFlags } from 'iron/apps/experiments/src/components/hooks/query-feature-flag-list';
import { createEventDetectionBookmarkParams } from 'iron/apps/experiments/src/components/query-utils';
import { useExperimentSettings } from 'iron/apps/experiments/src/components/hooks/experiment-settings';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import { TranslationKey } from 'common/i18n';
import { SvgIconReact, SvgIconType } from 'common/components/svg-icon';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { getReportUrl } from 'common/report/util';
import { useCreateExperiment } from 'iron/apps/experiments/src/components/hooks/create-experiment';
import { InputSize, InputType } from 'common/components/input/types';
import { InputReact } from 'common/components/input';
import styles from './index.module.styl';
import { CollectionMethodComponents } from './collection-method';
import { ExperimentPageNewExperimentSetting } from './_setting';
const DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss";
const COLLECTION_METHODS: Array<{
  icon: SvgIconType;
  title: TranslationKey;
  description: TranslationKey;
  learnMore: string;
  value: ExperimentCollectionType;
}> = [{
  icon: "feature-flag",
  title: "experiments.new.source-options.feature-flag-title",
  description: "experiments.new.source-options.feature-flag-description",
  learnMore: "#",
  value: ExperimentCollectionType.FEATURE_FLAG
}, {
  icon: "event",
  title: "experiments.new.source-options.exposure-events-title",
  description: "experiments.new.source-options.exposure-events-description",
  learnMore: "#",
  value: ExperimentCollectionType.EXPOSURE_EVENTS
}];
export function ExperimentPageNewExperiment() {
  var _stagedExperiment$set4;
  const i18n = useTranslation();
  const {
    pathname,
    search,
    hash
  } = getLocation();
  const navigate = useNavigate();
  const {
    stagedExperiment,
    stageChanges
  } = useStagedExperiment();
  const {
    stagedFeatureFlag
  } = useQueryFeatureFlags();
  const {
    projectId,
    workspaceId,
    featureFlagIntegrationEnabled
  } = useExperimentPageContext();
  const experimentSettings = useExperimentSettings();
  const createExperiment = useCreateExperiment();
  const handleCollectionMethod = React.useCallback((collectionMethod: ExperimentCollectionType) => {
    stageChanges({
      settings: {
        collectionMethod,
        controlKey: "",
        exposureEventSettings: {
          experimentName: ""
        },
        featureFlagSettings: {
          featureFlagId: ""
        }
      }
    });
  }, [stageChanges]);
  const handleEnterHypothesis = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = ev.target.value.trim();
    stageChanges({
      hypothesis: value
    });
  };
  const canCreate = React.useMemo(() => {
    var _stagedExperiment$set, _stagedExperiment$set2, _stagedExperiment$set3;
    if (!stagedExperiment) {
      return false;
    }
    if (!stagedExperiment.name || stagedExperiment.name.trim() === "") {
      return false;
    }
    switch (stagedExperiment.settings.collectionMethod) {
      case ExperimentCollectionType.FEATURE_FLAG:
        return !!((_stagedExperiment$set = stagedExperiment.settings.featureFlagSettings) !== null && _stagedExperiment$set !== void 0 && _stagedExperiment$set.featureFlagKey) || !!((_stagedExperiment$set2 = stagedExperiment.settings.featureFlagSettings) !== null && _stagedExperiment$set2 !== void 0 && _stagedExperiment$set2.featureFlagId);
      case ExperimentCollectionType.EXPOSURE_EVENTS:
        return !!((_stagedExperiment$set3 = stagedExperiment.settings.exposureEventSettings) !== null && _stagedExperiment$set3 !== void 0 && _stagedExperiment$set3.experimentName);
    }
  }, [stagedExperiment]);
  const handleCancel = React.useCallback(() => {
    navigate(getReportUrl("experiments", {
      projectId,
      workspaceId
    }));
  }, [projectId, workspaceId, navigate]);
  const handleCreate = React.useCallback(() => {
    if (!canCreate) {
      return;
    }
    createExperiment();
  }, [canCreate, createExperiment]);
  const eventDetectionBookmarkParams = React.useMemo(() => {
    var _experimentSettings$n;
    if (!stagedExperiment) {
      return undefined;
    }
    return createEventDetectionBookmarkParams({
      experiment: stagedExperiment as Experiment,
      featureFlag: stagedFeatureFlag,
      experimentNameProperty: (_experimentSettings$n = experimentSettings.nameProperty) === null || _experimentSettings$n === void 0 ? void 0 : _experimentSettings$n.value
    });
  }, [stagedExperiment, stagedFeatureFlag, experimentSettings]);
  const {
    data: eventDetectionQueryResults
  } = useQuery({
    queryKey: eventDetectionQueryKey({
      projectId: projectId!,
      workspaceId: workspaceId!,
      experimentId: stagedExperiment.id,
      bookmarkParams: eventDetectionBookmarkParams
    }),
    enabled: !!eventDetectionBookmarkParams,
    queryFn: () => {
      const queryParams: {
        project_id: string;
        workspace_id?: string;
      } = {
        project_id: String(projectId)
      };
      if (workspaceId) {
        queryParams["workspace_id"] = String(workspaceId);
      }
      return queryModule.executeReportQuery({
        endpoint: BookmarkType.Insights,
        queryParams,
        body: {
          bookmark: eventDetectionBookmarkParams,
          use_query_cache: true,
          use_query_sampling: true,
          report_query_origin: BookmarkType.Insights,
          tracking_props: {}
        },
        throwOnInvalidStatus: true,
        location: {
          pathname,
          search,
          hash,
          key: "",
          state: ""
        }
      }).then(response => {
        return response.json();
      });
    }
  });
  const eventDates = React.useMemo(() => {
    var _Object$values$, _Object$values, _find, _findLast;
    if (!eventDetectionQueryResults) {
      return [undefined, undefined];
    }
    const dateCounts = (_Object$values$ = (_Object$values = Object.values(eventDetectionQueryResults === null || eventDetectionQueryResults === void 0 ? void 0 : eventDetectionQueryResults.series)) === null || _Object$values === void 0 ? void 0 : _Object$values[0]) !== null && _Object$values$ !== void 0 ? _Object$values$ : {};
    const dateCountEntries = Object.entries(dateCounts).sort((a, b) => {
      if (a[0] < b[0]) {
        return -1;
      }
      if (a[0] > b[0]) {
        return 1;
      }
      return 0;
    });
    return [(_find = find(dateCountEntries, entry => entry[1] > 0)) === null || _find === void 0 ? void 0 : _find[0], (_findLast = findLast(dateCountEntries, entry => entry[1] > 0)) === null || _findLast === void 0 ? void 0 : _findLast[0]];
  }, [eventDetectionQueryResults]);
  React.useEffect(() => {
    if (!(stagedExperiment !== null && stagedExperiment !== void 0 && stagedExperiment.start_date) && !!eventDates[0]) {
      const updates: Partial<Experiment> = {
        start_date: moment(eventDates[0]).format(DATE_FORMAT)
      };
      stageChanges(updates);
    }
  }, [stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.start_date, eventDates, stageChanges]);
  const CollectionMethodComponent = CollectionMethodComponents[(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set4 = stagedExperiment.settings) === null || _stagedExperiment$set4 === void 0 ? void 0 : _stagedExperiment$set4.collectionMethod) as keyof typeof CollectionMethodComponents];
  return <div className={styles.bg} data-sentry-component="ExperimentPageNewExperiment" data-sentry-source-file="index.tsx">
      <div className={styles.modal}>
        <div className={styles.header}>{i18n.t("experiments.new.header")}</div>
        <div className={styles.content}>
          {featureFlagIntegrationEnabled && <ExperimentPageNewExperimentSetting label={i18n.t("experiments.new.source")}>
              <div className={styles.sourceOptions}>
                {COLLECTION_METHODS.map(option => {
              var _stagedExperiment$set5;
              return <SourceOption key={option.value} icon={option.icon} title={option.title} description={option.description} learnMore={option.learnMore} value={option.value} selected={(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set5 = stagedExperiment.settings) === null || _stagedExperiment$set5 === void 0 ? void 0 : _stagedExperiment$set5.collectionMethod) === option.value} onSelect={handleCollectionMethod} />;
            })}
              </div>
            </ExperimentPageNewExperimentSetting>}
          {CollectionMethodComponent && <CollectionMethodComponent />}
          {canCreate && <ExperimentPageNewExperimentSetting label={i18n.t("experiments.new.hypothesis")} subLabel={i18n.t("common.optional")}>
              <InputReact className={styles.fullWidth} value={(stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.hypothesis) || ""} size={InputSize.Large} type={InputType.TextArea} onInput={(ev: TODO) => handleEnterHypothesis(ev)} />
            </ExperimentPageNewExperimentSetting>}
        </div>
        <div className={styles.footer}>
          <ButtonReact theme={ButtonTheme.Tertiary} onClick={() => {
          handleCancel();
        }} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {i18n.t("common.cancel")}
          </ButtonReact>
          <ButtonReact theme={ButtonTheme.Primary} onClick={() => {
          handleCreate();
        }} disabled={!canCreate} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {i18n.t("experiments.new.create")}
          </ButtonReact>
        </div>
      </div>
    </div>;
}
function SourceOption(_ref: {
  icon: SvgIconType;
  title: TranslationKey;
  description: TranslationKey;
  learnMore: string;
  value: ExperimentCollectionType;
  selected?: boolean;
  onSelect?: (value: ExperimentCollectionType) => void;
}) {
  let {
    icon,
    title,
    description,
    learnMore,
    value,
    selected,
    onSelect
  }: {
    icon: SvgIconType;
    title: TranslationKey;
    description: TranslationKey;
    learnMore: string;
    value: ExperimentCollectionType;
    selected?: boolean;
    onSelect?: (value: ExperimentCollectionType) => void;
  } = _ref;
  const i18n = useTranslation();
  return <div className={clsx(styles.sourceOption, {
    [styles.selected]: selected
  })} onClick={() => onSelect === null || onSelect === void 0 ? void 0 : onSelect(value)} role='button' tabIndex={0} onKeyDown={ev => {
    if (ev.key === "Enter" || ev.key === " ") {
      onSelect === null || onSelect === void 0 || onSelect(value);
    }
  }} data-sentry-component="SourceOption" data-sentry-source-file="index.tsx">
      <div className={styles.icon}>
        <SvgIconReact icon={icon} data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
      </div>
      <div className={styles.title}>{i18n.t(title)}</div>
      <div className={styles.description}>{i18n.t(description)}</div>
      <div>
        <a href={learnMore} className={styles.learnMore} onClick={ev => ev.stopPropagation()} target='_blank'>
          {i18n.t("common.learn-more")}
        </a>
      </div>
    </div>;
}